import React, { useEffect, useState } from 'react';
import { useLocationPermission } from '../hooks/useLocationPermission';
import { MapPinIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface LocationPermissionGateProps {
  children: React.ReactNode;
}

export const LocationPermissionGate: React.FC<LocationPermissionGateProps> = ({ children }) => {
  const { permissionStatus, isRequesting, error, requestPermission } = useLocationPermission();
  const [hasRequested, setHasRequested] = useState(false);

  // Auto-request permission on mount
  useEffect(() => {
    if (permissionStatus === 'prompt' && !hasRequested) {
      setHasRequested(true);
      requestPermission();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionStatus, hasRequested]);

  // If permission is granted, show children
  if (permissionStatus === 'granted') {
    return <>{children}</>;
  }

  // If requesting, show loading
  if (isRequesting || (permissionStatus === 'prompt' && !hasRequested)) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 50%, #d1d5db 100%)'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          padding: '32px',
          maxWidth: '480px',
          textAlign: 'center' as const,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 16px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #ee4238, #af201c)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <MapPinIcon style={{ width: '32px', height: '32px', color: 'white' }} />
          </div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#111827', marginBottom: '8px' }}>
            Requesting Location Access
          </div>
          <div style={{ color: '#6b7280', fontSize: '14px', marginTop: '8px' }}>
            Please allow location access to continue using the app.
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid rgba(238, 66, 56, 0.3)',
            borderTop: '3px solid #ee4238',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '24px auto 0'
          }}></div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // If denied, show error message with retry option
  if (permissionStatus === 'denied') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 50%, #d1d5db 100%)'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          padding: '32px',
          maxWidth: '480px',
          textAlign: 'center' as const,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 16px',
            borderRadius: '50%',
            background: '#fee2e2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ExclamationTriangleIcon style={{ width: '32px', height: '32px', color: '#dc2626' }} />
          </div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#111827', marginBottom: '8px' }}>
            Location Permission Required
          </div>
          <div style={{ color: '#6b7280', fontSize: '14px', marginTop: '8px', lineHeight: '1.5' }}>
            {error || 'Location access is required to use this app. Please enable location permission in your browser settings.'}
          </div>
          <div style={{ 
            marginTop: '24px', 
            padding: '16px',
            background: '#f9fafb',
            borderRadius: '12px',
            textAlign: 'left' as const
          }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
              How to enable location:
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>
              <div style={{ marginBottom: '4px' }}>• <strong>Chrome/Edge:</strong> Click the lock icon in address bar → Site settings → Location → Allow</div>
              <div style={{ marginBottom: '4px' }}>• <strong>Firefox:</strong> Click the shield icon → Permissions → Location → Allow</div>
              <div style={{ marginBottom: '4px' }}>• <strong>Safari:</strong> Safari → Settings → Websites → Location → Allow</div>
              <div>• <strong>Mobile:</strong> Settings → Apps → Browser → Permissions → Location → Allow</div>
            </div>
          </div>
          <div style={{ marginTop: '24px' }}>
            <button
              onClick={() => {
                setHasRequested(false);
                requestPermission();
              }}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #ee4238, #af201c)',
                border: 'none',
                color: '#fff',
                borderRadius: '12px',
                fontWeight: 800,
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'opacity 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Unknown state - show loading
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 50%, #d1d5db 100%)'
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        padding: '32px',
        maxWidth: '480px',
        textAlign: 'center' as const,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid rgba(238, 66, 56, 0.3)',
          borderTop: '3px solid #ee4238',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }}></div>
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#6b7280' }}>
          Checking location permission...
        </div>
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

